import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { setUpResetPassword } from "../services/store/authentication.actions";
import { LoadingSpinnerButton } from "../../_UI/Components/PathwayButtons";
import styled from "styled-components";
import { ErrorMessage, SuccessMessage } from "../../_UI/Components/Alerts/PathwayAlertMessages";
import { authenticationReducerActions } from "../../_services/store";
import PathwayTextInputGroup from "../../_UI/Components/Inputs/PathwayTextInputGroup";
import { Link } from "react-router-dom";

const ForgotPasswordScreen = () => {
    const dispatch = useDispatch();
    const authentication = useSelector((state) => state.authentication);

    const resetPassword = (e) => {
        e.preventDefault();
        dispatch(setUpResetPassword(authentication.resetMail));
    };

    const handleResetMailChange = (e) => {
        const { value } = e.target;
        dispatch(authenticationReducerActions.setResetMail(value));
    };

    return (
        <SplitScreenContainer>
            <LeftSection>
                <LeftLogoContainer>
                     <HeaderText>
                        Passwort vergessen?<br/>Kein Problem!
                    </HeaderText>
                    <img
                        src="/astronaut_thinking.png"
                        alt="Pathway Logo"
                        style={{ maxWidth: "200px", marginTop: "2rem" }}
                        className="rounded mx-auto d-block"
                    />
                </LeftLogoContainer>
                <LegalFooter>
                    <LegalLink
                        href="https://www.pathway-solutions.de/impressum"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Impressum
                    </LegalLink>
                    <LegalSeparator>•</LegalSeparator>
                    <LegalLink
                        href="https://app.pathway-solutions.de/Nutzungsvereinbarung_Platform.pdf"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Nutzungsvereinbarung
                    </LegalLink>
                    <LegalSeparator>•</LegalSeparator>
                    <LegalLink
                        href="https://app.pathway-solutions.de/AVV_Platform.pdf"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Datenschutz
                    </LegalLink>
                </LegalFooter>
            </LeftSection>

            <RightSection>
                <ForgotPasswordFormContainer>
                    <img
                        src="/logo_black.png"
                        alt="Pathway Logo"
                        style={{ maxWidth: "200px", marginBottom: "2rem" }}
                        className="rounded mx-auto d-block"
                    />
                    
                    <SubtitleText>
                        Wir schicken dir eine E-Mail zum Zurücksetzen deines Passworts
                    </SubtitleText>

                    <ForgotPasswordContent>
                        <InputFieldContainer>
                            <FieldLabel>E-Mail</FieldLabel>
                            <PathwayTextInputGroup
                                type="mail"
                                name="resetMail"
                                placeHolder="Deine E-Mail-Adresse"
                                handleChange={handleResetMailChange}
                                value={authentication.resetMail}
                                autoFocus
                            />
                        </InputFieldContainer>
                        
                        {authentication.resetErrorMessage && (
                            <ErrorMessage marginless>{authentication.resetErrorMessage}</ErrorMessage>
                        )}
                        {authentication.resetSuccessMessage && (
                            <SuccessMessage marginless>{authentication.resetSuccessMessage}</SuccessMessage>
                        )}
                        
                        <ButtonContainer>
                            <LoadingSpinnerButton onClick={resetPassword} loading={!!authentication.resetLoading}>
                                Passwort zurücksetzen
                            </LoadingSpinnerButton>
                        </ButtonContainer>
                        
                        <BackToLoginContainer>
                            <BackToLoginLink to="/login">
                                ← Zurück zum Login
                            </BackToLoginLink>
                        </BackToLoginContainer>
                    </ForgotPasswordContent>

                    <CompanyFooter>
                        <CompanyName>pathway solutions gmbh</CompanyName>
                    </CompanyFooter>
                </ForgotPasswordFormContainer>
            </RightSection>
        </SplitScreenContainer>
    );
};

export default ForgotPasswordScreen;

const SplitScreenContainer = styled.div`
    display: flex;
    height: 100vh;
    width: 100vw;
    
    @media (max-width: 768px) {
        flex-direction: column;
        height: auto;
        min-height: 100vh;
    }
`;

const LeftSection = styled.div`
    flex: 1;
    background: #EFFBFF;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 2rem;
    
    @media (max-width: 768px) {
        flex: none;
        padding: 1.5rem 1rem;
        min-height: 40vh;
        justify-content: flex-start;
        order: 2; /* Move to bottom on mobile */
    }
`;

const LeftLogoContainer = styled.div`
    margin-bottom: 2rem;
    
    @media (max-width: 768px) {
        margin-bottom: 1rem;
    }
`;

const HeaderText = styled.h2`
    font-family: 'Inter', sans-serif;
    font-size: 28px;
    font-weight: 600;
    text-align: center;
    color: #1f2937;
    margin-bottom: 50px;
    line-height: 1.2;
    
    @media (max-width: 768px) {
        font-size: 22px;
        margin-bottom: 1.5rem;
        margin-top: 1rem;
    }
`;

const RightSection = styled.div`
    flex: 1;
    background: white;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 2rem;
    
    @media (max-width: 768px) {
        flex: none;
        padding: 1rem;
        align-items: flex-start;
        min-height: 60vh;
        order: 1; /* Move to top on mobile */
    }
`;

const ForgotPasswordFormContainer = styled.div`
    width: 100%;
    max-width: 400px;
    
    @media (max-width: 768px) {
        max-width: 100%;
        padding: 0;
    }
`;

const SubtitleText = styled.p`
    font-family: 'Inter', sans-serif;
    font-size: 14px;
    text-align: center;
    color: #6B7280;
    margin-bottom: 3rem;
    line-height: 1.5;
    
    @media (max-width: 768px) {
        margin-bottom: 2rem;
    }
`;

const ForgotPasswordContent = styled.div`
    display: grid;
    row-gap: 1.5rem;
`;

const InputFieldContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    
    input {
        height: 45px !important;
    }
`;

const FieldLabel = styled.label`
    font-family: 'Inter', sans-serif;
    font-size: 16px;
    color: #1f2937;
    font-weight: 500;
    text-align: left;
`;

const ButtonContainer = styled.div`
    margin-top: 1rem;
    
    button {
        width: 100%;
        height: 45px;
        font-size: 16px;
    }
`;

const BackToLoginContainer = styled.div`
    text-align: center;
    margin-top: 1.5rem;
    
    @media (max-width: 768px) {
        margin-top: 1rem;
    }
`;

const BackToLoginLink = styled(Link)`
    font-family: 'Inter', sans-serif;
    font-size: 14px;
    color: #6B7280;
    text-decoration: none;
    transition: color 0.2s ease;
    
    &:hover {
        color: #374151;
        text-decoration: underline;
    }
`;

const CompanyFooter = styled.div`
    margin-top: 3rem;
    text-align: center;
    
    @media (max-width: 768px) {
        margin-top: 2rem;
    }
`;

const CompanyName = styled.div`
    font-family: 'Inter', sans-serif;
    font-size: 14px;
    color: #6B7280;
    text-align: center;
    margin-bottom: 0.5rem;
    
    @media (max-width: 768px) {
        font-size: 12px;
        margin-bottom: 1rem;
    }
`;

const LegalLink = styled.a`
    font-family: 'Inter', sans-serif;
    font-size: 12px;
    color: #6B7280;
    text-decoration: none;
    transition: color 0.2s ease;
    
    &:hover {
        color: #374151;
        text-decoration: underline;
    }
    
    @media (max-width: 768px) {
        font-size: 11px;
    }
`;

const LegalFooter = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
    margin-top: 2rem;
    padding: 1rem;
    
    @media (max-width: 768px) {
        margin-top: auto;
        padding: 0.5rem;
        gap: 0.5rem;
        flex-wrap: wrap;
    }
`;

const LegalSeparator = styled.span`
    font-family: 'Inter', sans-serif;
    font-size: 12px;
    color: #9CA3AF;
`;